import { faCheck, faFileUpload, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { useDisclosureContext } from '@vivli/features/disclosures/infrastructure/context';
import { TextAreaFormFieldComponent } from '@vivli/shared/components';
import { GridCellRendererEnum } from '@vivli/shared/infrastructure/enum';
import { ButtonView, GridView } from '@vivli/shared/views';
import { ColDef, IDateFilterParams } from 'ag-grid-community';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import './feedback-form.style.css';
import { IContributingOrgDetail, IParticipatingOrganizationDto } from '@vivli/features/disclosures/infrastructure/interface';
import moment from 'moment';
import { useOrgFiles } from '@vivli/features/disclosures/infrastructure/hook';
import { useModalApiContext } from '@vivli/core/infrastructure/context';
import { IDataPackageFileDescriptor } from '@vivli/shared/features/data-package/infrastructure/interface';
import { DataPackageUploaderComponent } from '@vivli/shared/features/data-package/components';
import { IFileUploader } from '@vivli/shared/infrastructure/hook';

const containerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateRows: 'auto',
    rowGap: '20px'
};

const btnStyle: CSSProperties = {
    height: '30px',
    padding: '5px 15px',
    minWidth: 'initial'
};

const adminStyle: CSSProperties = {
    backgroundColor: 'rgb(255, 246, 217)'
};

const textAreaStyle: CSSProperties = {
    display: 'grid',
    gridTemplateRows: 'auto auto',
    rowGap: '1em'
};

const dcOrgsHeaderStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    columnGap: '1em',
    alignItems: 'center'
};

const reviewDatesStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    columnGap: '20px',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    padding: '10px'
};

const attachmentsModalStyle: CSSProperties = {
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    rowGap: '20px'
};

const headerBodyStyle: CSSProperties = {
    display: 'grid',
    gridTemplateRows: 'auto auto',
    rowGap: '20px'
};

const commentsStyle: CSSProperties = {
    boxShadow: '0px 0px 5px 0px rgba(5, 61, 88,0.05)',
    backgroundColor: 'rgba(5, 61, 88,0.05)',
    padding: '10px'
};

const modalStyle: CSSProperties = {
    minWidth: '1000px',
    padding: '20px',
    textAlign: 'left',
    height: '80%'
};

export const FeedbackFormView = () => {
    const {
        onFinalizedClick,
        onFinalizedAllClick,
        permissions,
        dataRequest,
        user,
        documentsForFeedbackUri,
        attachmentsPackageApi,
        disclosure,
        organizations,
        refreshApiToken
    } = useDisclosureContext();
    const { dataPackage, onFileDeleted, onPreExistingFile, onFileValidation, files, setAdditionalFileProps } = attachmentsPackageApi;
    const [uploader, setUploader] = useState<IFileUploader>();
    const [contributingOrgs, setContributingOrgs] = useState<IContributingOrgDetail[]>([]);
    const uploadingOrgRef = useRef<IParticipatingOrganizationDto>();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const modalService = useModalApiContext();
    const filesRef = useRef<IDataPackageFileDescriptor[]>(files || []);

    const contributingOrgDetails: IContributingOrgDetail[] = useWatch({ name: 'contributingOrgDetails' });

    const filesToShow = useOrgFiles(files, user, permissions?.viewAllAttachments);

    const getOrgFiles = (orgId: string) => {
        // undefined means dp is still loading
        if (dataPackage === null) {
            // return empty array if dp failed to load
            return [];
        }

        return files?.filter((file) => file.organizationId === orgId);
    };

    const handleUploadClick = (orgDto: IParticipatingOrganizationDto, e) => {
        uploadingOrgRef.current = orgDto;

        setAdditionalFileProps({
            organizationId: uploadingOrgRef.current?.orgId,
            organizationName: uploadingOrgRef.current?.organizationName
        });

        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && uploader) {
            uploader.upload(e.target.files);
            // reset value so the same file can be selected again if needed
            e.target.value = '';
        }
    };

    const handleViewAttachmentsClick = (orgDetail: IContributingOrgDetail) => {
        const filesToShow = getOrgFiles(orgDetail.organizationId);

        modalService.custom(
            <div style={attachmentsModalStyle}>
                <div style={headerBodyStyle}>
                    <h3>Comments:</h3>
                    <div style={commentsStyle}>{orgDetail?.feedbackComment || 'No Comments Found.'}</div>
                </div>
                <div style={headerBodyStyle}>
                    <h3>Attachments:</h3>
                    {/* onUploaderInit goes AFTER the spread: attachmentsPackageApi carries its own, so
                        listing it first lets the spread overwrite it and `uploader` never gets set. */}
                    <DataPackageUploaderComponent
                        user={user}
                        uploadUri={documentsForFeedbackUri}
                        allowDownload={true}
                        files={filesToShow}
                        refreshApiToken={refreshApiToken}
                        {...attachmentsPackageApi}
                        onUploaderInit={setUploader}
                        onFileValidation={(fileName) => handlePreExistingFile(fileName)}
                        pageSize={5}
                        usePagination={true}
                    />
                </div>
            </div>,
            {
                showCloseButton: true,
                style: modalStyle
            }
        );
    };

    const handleFinalizedAllClick = () => {
        const orgDtos = contributingOrgs.map(getFinalizedOrgDto);
        onFinalizedAllClick && onFinalizedAllClick(orgDtos);
    };

    const handlePreExistingFile = (fileName: string) => {
        const preExistingFile = filesRef.current.find((f) => f.name === fileName);
        if (!preExistingFile) {
            return onFileValidation(fileName);
        }

        const fileOrgSameAsUserOrg = user.orgMemberships?.some((om) => om.orgId === preExistingFile?.organizationId && om.isEnabled);
        if (fileOrgSameAsUserOrg || permissions?.viewAllAttachments) {
            return onFileValidation(fileName);
        }

        const fileExistsInOtherOrgMsg = `There is already a feedback file with the name "${fileName}". Please rename the file and upload it again.`;
        return new Promise<boolean>((resolve, reject) => {
            modalService.message(fileExistsInOtherOrgMsg, { showBackground: false, showContinueButton: true });
            reject(false);
        });
    };

    const getFinalizedOrgDto = (organization: IContributingOrgDetail) => {
        const orgFiles = getOrgFiles(organization.organizationId);
        const orgStudies = disclosure?.studyDisclosureDetails
            ?.filter((s) => s.organizationId === organization.organizationId)
            ?.map((s) => {
                const study = dataRequest.requestedStudies.find((rs) => rs.studyId === s.studyId);

                return {
                    studyId: s.studyId,
                    studyTitle: study?.studyTitle
                };
            });

        const orgDto: IParticipatingOrganizationDto = {
            organizationName: organization.organizationName,
            orgId: organization.organizationId,
            finalizedReview: organization.finalized,
            feedbackComment: organization.feedbackComment,
            uploadedFiles: orgFiles,
            participatingStudies: orgStudies
        };
        return orgDto;
    };

    const formatDate = (value: any) => {
        return moment(value).format('DD-MMM-YYYY').replace('Invalid date', '');
    };

    const getFeedbackControls = ({
        showApprovalBtn,
        organization,
        isAdmin,
        showAttachmentsBtn
    }: {
        showApprovalBtn: boolean;
        showAttachmentsBtn: boolean;
        organization: IContributingOrgDetail;
        isAdmin: boolean;
    }) => {
        const columns = `${showApprovalBtn ? 'auto ' : ''}${showAttachmentsBtn ? 'auto ' : ''}${isAdmin ? 'auto ' : ''}`;

        const btnContainerStyle: CSSProperties = {
            display: 'grid',
            gridTemplateColumns: columns,
            columnGap: '10px',
            placeContent: 'center end',
            height: '100%'
        };

        const orgDto = getFinalizedOrgDto(organization);

        return (
            <div style={btnContainerStyle}>
                {showAttachmentsBtn && (
                    <ButtonView
                        title="View Comments and Attachments"
                        theme={'blue-on-white'}
                        style={btnStyle}
                        faIcon={faPaperclip}
                        onClick={() => handleViewAttachmentsClick(organization)}
                    />
                )}

                {isAdmin && (
                    <>
                        {showApprovalBtn && (
                            <ButtonView
                                title="Mark Review Completed"
                                theme={'green-on-white'}
                                style={btnStyle}
                                faIcon={faCheck}
                                onClick={() => onFinalizedClick(orgDto)}
                            />
                        )}
                        {/* The hidden file input this opens is rendered once at the component root, not
                            here. See the comment on it for why it must not be a child of this button. */}
                        <ButtonView
                            title="Upload Files on behalf of Org"
                            theme={'blue-on-white'}
                            style={btnStyle}
                            faIcon={faFileUpload}
                            onClick={(e) => handleUploadClick(orgDto, e)}
                        />
                    </>
                )}
            </div>
        );
    };

    const gridCols: ColDef<IContributingOrgDetail>[] = [
        {
            headerName: 'Name',
            field: 'organizationName',
            sort: 'asc',
            maxWidth: 200,
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: {
                allowCopy: true,
                copyText: 'Organization Name'
            }
        },
        {
            headerName: 'Comments',
            field: 'feedbackComment',
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: {
                allowCopy: true,
                copyText: 'Copy Comments to Clipboard'
            }
        },
        {
            headerName: 'Status',
            field: 'finalized',
            valueGetter: ({ data }) => {
                return data?.finalized ? 'Review Complete' : 'Awaiting Review';
            }
        },
        {
            headerName: 'Date Completed',
            field: 'updatedDate',
            filter: 'agDateColumnFilter',
            filterParams: {
                debounceMs: 500,
                includeTime: false
            } as IDateFilterParams,
            filterValueGetter: ({ data }) => {
                return new Date(data.updatedDate);
            },
            valueGetter: ({ data }) => {
                return data?.finalized ? new Date(data.updatedDate) : null;
            },
            valueFormatter: ({ value }) => formatDate(value)
        },
        {
            headerName: '',
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: ({ data }) => {
                const canClickAttachments =
                    user.orgMemberships.some((om) => om.orgId === data.organizationId) || permissions.viewAllAttachments;
                const showAttachmentsBtn =
                    (data?.uploadedFileNames?.length > 0 || data?.feedbackComment?.length > 0) && canClickAttachments;
                return {
                    component: getFeedbackControls({
                        showApprovalBtn: !data.finalized,
                        organization: data,
                        isAdmin: permissions.actAsOrg,
                        showAttachmentsBtn
                    })
                };
            },
            sortable: false,
            filter: false,
            suppressMovable: true,
            maxWidth: 170,
            minWidth: 100
        }
    ];

    const grid = (
        <GridView
            rowData={contributingOrgs}
            columns={gridCols}
            fullWidthColumns={true}
            emptyMessage={'No Data Contributor Organizations Found.'}
            autoHeight={true}
            rowHeight={50}
        />
    );

    const initializeData = () => {
        const uniqueOrgs = [...new Map(contributingOrgDetails.map((item) => [item['organizationId'], item])).values()];

        const orgData: IContributingOrgDetail[] = uniqueOrgs.map((c) => {
            const orgDetails = dataRequest.requestedStudies?.find((s) => s.organizationId === c.organizationId);
            const uploadedFileNames = dataPackage?.dataPackageFileDescriptors
                ?.filter((file) => file.organizationId === c.organizationId)
                .map((file) => file.name);

            return {
                ...c,
                organizationName: orgDetails?.organizationName,
                uploadedFileNames
            };
        });

        setContributingOrgs(orgData);
    };

    useEffect(() => {
        if (!organizations) {
            return;
        }
        initializeData();
    }, [organizations, contributingOrgDetails, dataPackage?.dataPackageFileDescriptors]);

    // Seed the upload target with the logged in user's own org. This deliberately does NOT live in
    // initializeData: that effect re-runs on every data package write, so it kept resetting the
    // target partway through a batch, and an admin uploading on behalf of an org got the second file
    // onward tagged with the wrong org. Once seeded, handleUploadClick owns the value. (DEV-392)
    useEffect(() => {
        if (!organizations || user?.orgMemberships.length === 0) {
            return;
        }

        const orgMembership = user.orgMemberships[0];
        const org = organizations.find((o) => orgMembership.orgId === o.id);

        setAdditionalFileProps({
            organizationId: org?.id,
            organizationName: org?.name
        });
    }, [organizations, user]);

    useEffect(() => {
        filesRef.current = files || [];
    }, [files]);

    return (
        <div style={containerStyle}>
            {/* One shared hidden input for every org row's "Upload Files on behalf of Org" button, kept
                deliberately OUTSIDE those buttons. ButtonView renders a div with onClick, so an input
                nested inside it received the programmatic .click() and then bubbled that click straight
                back into the button's own handler. Every row also assigned to this one ref, so the ref
                always pointed at the LAST row's input — meaning a click on any row re-ran
                handleUploadClick with the last row's org and overwrote the org the user actually chose.
                Out here there is no ancestor click handler to bubble into and only one input exists. */}
            <input type="file" multiple className={'file-input-hidden'} ref={fileInputRef} onChange={handleFileChange} />
            <div style={reviewDatesStyle}>
                <div>
                    <b>Review Start Date:</b> {formatDate(disclosure.reviewStartDate)}
                </div>
                <div>
                    <b>Review End Date:</b> {formatDate(disclosure.reviewEndDate)}
                </div>
            </div>
            {/*commented out Finalize Review for All Orgs button per decision prior to 3.8*/}
            {/*<div style={dcOrgsHeaderStyle}>*/}
            {/*    <div>Data Contributor Organizations</div>*/}
            {/*    {permissions.actAsOrg && disclosure.status === DisclosureStatusEnum.ThirtyDayReview && (*/}
            {/*        <ButtonView onClick={handleFinalizedAllClick}>Finalize Review for All Orgs</ButtonView>*/}
            {/*    )}*/}
            {/*</div>*/}
            {grid}
            {permissions.viewAttachments && (
                <>
                    <div>Shared Attachments</div>
                    {/* onUploaderInit goes AFTER the spread: attachmentsPackageApi carries its own, so
                        listing it first lets the spread overwrite it and `uploader` never gets set. */}
                    <DataPackageUploaderComponent
                        user={user}
                        uploadUri={documentsForFeedbackUri}
                        allowDelete={permissions.deleteAttachments}
                        allowOwnerDelete={permissions.allowOwnerDelete}
                        allowDownload={true}
                        allowUpload={permissions.uploadAttachments}
                        files={filesToShow}
                        preExistingFiles={dataPackage?.dataPackageFileDescriptors || []}
                        onFileDeleted={onFileDeleted}
                        hideSize={true}
                        showOrgName={true}
                        refreshApiToken={refreshApiToken}
                        {...attachmentsPackageApi}
                        onUploaderInit={setUploader}
                        onFileValidation={(fileName) => handlePreExistingFile(fileName)}
                    />
                </>
            )}
            {permissions?.internalNotes && (
                <>
                    <div>Notes</div>
                    <div style={textAreaStyle}>
                        {/*{permissions?.canApprove && (*/}
                        {/*    <TextAreaFormFieldComponent name={'additionalNote'} label="Internal notes (Org Admins)" />*/}
                        {/*)}*/}
                        {permissions.internalNotes && (
                            <TextAreaFormFieldComponent
                                style={adminStyle}
                                name={'internalNote'}
                                label="Disclosure Internal notes (Vivli Admins)"
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
